import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Play, RotateCcw, Pause, Download, Loader2 } from "lucide-react";
import GIF from "gif.js";
import { toast } from "sonner";
import { buildLevel, W, H } from "@/game/engine";
import { seedFromString, todayUtcKey, withSeededMathRandom } from "@/lib/seededRng";
import { ghostPositionAt } from "@/lib/replay";

// Simple 2D playback viewer for a daily-challenge run.
// Renders the seeded outdoor lot layout once + animates a dot along the replay.
export default function ReplayViewerDialog({ open, onOpenChange, playerName, score, replay }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const startedRef = useRef(0);
  const layoutRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [tSec, setTSec] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const totalSec = replay && replay.length > 0 ? replay[replay.length - 1][0] : 0;

  // Build seeded layout once (outdoor lot with today's daily seed).
  useEffect(() => {
    if (!open) return;
    const seed = seedFromString(todayUtcKey());
    layoutRef.current = withSeededMathRandom(seed, () => buildLevel("outdoor"));
  }, [open]);

  // Draw a single frame at time `elapsed` onto the given canvas context.
  const drawSceneAt = (ctx, elapsed) => {
    const layout = layoutRef.current;
    if (!layout) return;
    ctx.fillStyle = "#1c1e24";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#2a2e35";
    for (const w of layout.walls) ctx.fillRect(w.x, w.y, w.w, w.h);
    for (const o of layout.obstacles) {
      ctx.fillStyle = o.color || "#3a3f47";
      ctx.fillRect(o.x, o.y, o.w, o.h);
    }
    ctx.fillStyle = "#39FF14";
    for (const e of layout.exits) ctx.fillRect(e.x, e.y, e.w, e.h);
    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    let started = false;
    for (const [t, x, y] of replay) {
      if (t > elapsed) break;
      const px = x + 7;
      const py = y + 7;
      if (!started) { ctx.moveTo(px, py); started = true; }
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    const g = ghostPositionAt(replay, elapsed);
    if (g) {
      ctx.save();
      ctx.shadowColor = "rgba(255,255,255,0.9)";
      ctx.shadowBlur = 14;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(g.x + 7, g.y + 7, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  };

  // Render loop
  useEffect(() => {
    if (!open || !replay || replay.length === 0) return;
    startedRef.current = performance.now();
    setTSec(0);

    const drawFrame = () => {
      const ctx = canvasRef.current?.getContext("2d");
      if (!ctx) return;
      const now = performance.now();
      let elapsed = (now - startedRef.current) / 1000;
      if (elapsed > totalSec) elapsed = totalSec;
      setTSec(elapsed);
      drawSceneAt(ctx, elapsed);
      if (playing && elapsed < totalSec) {
        rafRef.current = requestAnimationFrame(drawFrame);
      }
    };

    rafRef.current = requestAnimationFrame(drawFrame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [open, replay, playing, totalSec]);

  const restart = () => {
    startedRef.current = performance.now();
    setPlaying(true);
  };

  const exportGif = async () => {
    if (!replay || replay.length === 0 || exporting) return;
    setExporting(true);
    setExportProgress(0);
    try {
      // Off-screen canvas for GIF frames (smaller size to keep file reasonable)
      const scale = 0.5;
      const gw = Math.round(W * scale);
      const gh = Math.round(H * scale);
      const off = document.createElement("canvas");
      off.width = W;
      off.height = H;
      const octx = off.getContext("2d");

      const gif = new GIF({
        workers: 2,
        quality: 12,
        width: gw,
        height: gh,
        workerScript: "/gif.worker.js",
        transparent: null,
      });

      const fps = 15;
      const totalFrames = Math.ceil(totalSec * fps);
      // Add frames — sample at fixed timestamps across the replay
      for (let i = 0; i < totalFrames; i++) {
        const t = (i / fps);
        drawSceneAt(octx, t);
        // Downscale to gw×gh via a second canvas
        const small = document.createElement("canvas");
        small.width = gw;
        small.height = gh;
        small.getContext("2d").drawImage(off, 0, 0, gw, gh);
        gif.addFrame(small, { delay: Math.round(1000 / fps), copy: false });
      }

      gif.on("progress", (p) => setExportProgress(Math.round(p * 100)));
      gif.on("finished", (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `parking-plot-escape-daily-${todayUtcKey()}-${(playerName || "ghost").toLowerCase()}.gif`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setExporting(false);
        setExportProgress(0);
        toast.success("GIF downloaded");
      });
      gif.render();
    } catch (e) {
      setExporting(false);
      toast.error("Failed to export GIF");
    }
  };

  if (!replay) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="replay-viewer-dialog"
        className="max-w-3xl bg-black/90 backdrop-blur-2xl border border-white/10 text-white rounded-none"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tighter flex items-center gap-3">
            GHOST · {playerName}
            <span className="text-white/50 text-sm tracking-[0.3em] font-mono">
              {String(score).padStart(6, "0")}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="relative border border-cyan-300/30 bg-black">
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            className="block w-full h-auto"
            style={{ imageRendering: "pixelated" }}
          />
          <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between text-[10px] tracking-[0.3em] text-white/70">
            <span>{tSec.toFixed(1)}s / {totalSec.toFixed(1)}s{exporting && ` · encoding ${exportProgress}%`}</span>
            <div className="flex gap-2">
              <button
                data-testid="replay-restart"
                onClick={restart}
                className="flex items-center gap-1 px-3 py-1.5 border border-white/30 hover:border-cyan-300 hover:text-cyan-300 transition-colors"
              >
                <RotateCcw size={12} /> REPLAY
              </button>
              <button
                data-testid="replay-toggle"
                onClick={() => setPlaying((v) => !v)}
                className="flex items-center gap-1 px-3 py-1.5 border border-white/30 hover:border-[#FFD400] hover:text-[#FFD400] transition-colors"
              >
                {playing ? <><Pause size={12} /> PAUSE</> : <><Play size={12} /> RESUME</>}
              </button>
              <button
                data-testid="replay-export-gif"
                onClick={exportGif}
                disabled={exporting}
                className="flex items-center gap-1 px-3 py-1.5 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black transition-colors disabled:opacity-50"
                style={{ boxShadow: "0 0 12px rgba(255,212,0,0.35)" }}
              >
                {exporting ? <><Loader2 size={12} className="animate-spin" /> {exportProgress}%</> : <><Download size={12} /> SAVE GIF</>}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
